import {
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuList,
  Portal,
} from "@chakra-ui/react";
import { ChangeEvent, useState } from "react";
import { BiSort } from "react-icons/bi";
import { FaChevronDown } from "react-icons/fa";
import { IoSearchOutline } from "react-icons/io5";
import { useAtomValue } from "jotai";
import { FilterMenuButton } from "../../components/menu";
import FilterBarMenuItem from "../../components/menu/menu-item";
import {
  postsControllerAtom,
  PostsFilterOption,
  PostsSortOption,
} from "./use-posts-filter";
import { useSearchParams } from "next/navigation";
import { capitalCase } from "change-case";

interface PostsFilterBarProps {
  onFilterChange: (e: any) => void;
  filterOptions: PostsFilterOption[] | any;
  buttonColors: string[];
  onSortChange?: (e: any) => void;
  sortOptions?: PostsSortOption[] | any;
  onSearchChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  setCurrentPage?: (num: number) => void;
  from?: string;
}

export const PostsFilterBar = ({
  onSortChange,
  onFilterChange,
  onSearchChange,
  filterOptions,
  sortOptions,
  buttonColors,
  setCurrentPage,
  from,
}: PostsFilterBarProps) => {
  const postController = useAtomValue(postsControllerAtom);
  const searchParams = useSearchParams();
  const preselectedFilter = searchParams?.get("selectedFilter");
  const [activeButton, setActiveButton] = useState(
    preselectedFilter ? capitalCase(preselectedFilter) : "All",
  );
  return (
    <>
      <Flex
        mt={from === "knowledge-center" ? 8 : 0}
        mx="auto"
        w={{
          base: "xs",
          md: "2xl",
        }}
        flexDirection={{
          base: "column",
          md: "row",
        }}
      >
        {from === "knowledge-center" ? (
          <>
            <InputGroup
              w={{
                base: "100%",
                md: "75%",
              }}
              bgColor="gray.100"
              borderLeftRadius={20}
              borderRightRadius={{ base: 20, md: 0 }}
              overflow="hidden"
              color="gray.500"
              mb={{
                base: "2",
                md: "0",
              }}
            >
              <InputLeftElement
                pointerEvents="none"
                children={<IoSearchOutline />}
              />
              <Input
                border="none"
                fontSize={{
                  base: "sm",
                  md: 14,
                }}
                _focusVisible={{
                  outline: "none",
                }}
                placeholder="What are you looking for?"
                _placeholder={{ opacity: 1, color: "gray.500" }}
                onChange={onSearchChange}
                value={postController.search}
              />
            </InputGroup>

            <Menu isLazy>
              <FilterMenuButton
                borderLeftRadius={{ base: 20, md: 0 }}
                borderRightRadius={{ base: 20, md: 20 }}
                leftIcon={<BiSort />}
                rightIcon={<FaChevronDown />}
                value={postController.sort}
              >
                {postController.sort}
              </FilterMenuButton>

              <Portal>
                <MenuList zIndex={10}>
                  {sortOptions.map((option: string, idx: number) => (
                    <FilterBarMenuItem
                      key={`${option}-${idx}`}
                      value={option}
                      onClick={onSortChange}
                    >
                      {option}
                    </FilterBarMenuItem>
                  ))}
                </MenuList>
              </Portal>
            </Menu>
          </>
        ) : (
          <></>
        )}
      </Flex>
      <Flex
        justifyContent={"center"}
        alignItems="center"
        mt={8}
        ml={0}
        gap={4}
        flexDirection={{ base: "column", md: "row" }}
        flexWrap={{
          md: "wrap",
        }}
      >
        {filterOptions.map((option: string, idx: number) => (
          <Button
            key={idx}
            id={`filterButton${idx}`}
            w="175px"
            borderRadius={30}
            onClick={(e) => {
              setActiveButton(e.currentTarget.value);
              onFilterChange(e);
              setCurrentPage && setCurrentPage(1);
            }}
            bgColor={buttonColors[idx]}
            value={option}
            color={activeButton === option ? "white" : "black"}
          >
            {option}
          </Button>
        ))}
      </Flex>
    </>
  );
};
